import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme, type ThemePreference } from "@/contexts/theme.context";
import { cn } from "@/lib/utils";

const options: {
  id: ThemePreference;
  label: string;
  description: string;
  icon: typeof Sun;
}[] = [
  {
    id: "system",
    label: "System",
    description: "Follow this device",
    icon: Monitor,
  },
  {
    id: "light",
    label: "Light",
    description: "Always light",
    icon: Sun,
  },
  {
    id: "dark",
    label: "Dark",
    description: "Always dark",
    icon: Moon,
  },
];

export function AppearanceSettings() {
  const { preference, resolved, setPreference } = useTheme();

  const status =
    preference === "system"
      ? resolved === "dark"
        ? "Matching your device, which is set to dark."
        : "Matching your device, which is set to light."
      : resolved === "dark"
        ? "Dark mode is on for your dashboard and account screens."
        : "Light mode is on for your dashboard and account screens.";

  return (
    <div className="space-y-4">
      <div
        role="radiogroup"
        aria-label="Color theme"
        className="grid gap-3 sm:grid-cols-3"
      >
        {options.map((option) => {
          const selected = preference === option.id;
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setPreference(option.id)}
              className={cn(
                "flex items-start gap-3 rounded-2xl border px-4 py-3.5 text-left transition-colors",
                selected
                  ? "border-primary bg-highlight text-foreground"
                  : "border-border bg-muted/50 text-foreground hover:bg-accent",
              )}
            >
              <span
                className={cn(
                  "mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full",
                  selected ? "bg-primary text-primary-foreground" : "bg-card text-foreground",
                )}
              >
                <Icon className="size-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold">{option.label}</span>
                <span className="mt-0.5 block text-xs font-medium text-muted-foreground">
                  {option.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-sm font-medium text-muted-foreground">{status}</p>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Your sales link keeps the colors you set for customers. This choice only changes Sqaleshop for you.
      </p>
    </div>
  );
}
